import {
  AssetType,
  BondType,
  InstrumentClass,
} from '@moment/trading-types/reference-data'

import { OrderSizeType } from '@/types/enums/order'

/**
 * Single source of truth for instrument classification across the trading UI.
 *
 * `AssetClass` is the granular discriminator the application branches on.
 * It is derived from the backend's three reference-data fields via
 * `getAssetClass(input)`:
 *
 *   - `assetType` — broad bucket (`'fixed_income' | 'equity' | 'fund'`)
 *   - `fixedIncomeType` — `BondType` enum, populated only for FI rows
 *   - `instrumentClass` — `InstrumentClass` enum, populated only for equity-shaped rows
 *
 * `assetType` alone is too coarse: at the broad-bucket level, ETFs and Mutual
 * Funds share `assetType: 'fund'`. The MF/ETF distinction lives in
 * `instrumentClass`, so render decisions for "is this specifically a mutual
 * fund?" MUST consult `AssetClass`, not `assetType`. See FR-044 in the
 * order-execution spec.
 *
 * Pre-hydration defense: `getAssetClass()` returns `null` when inputs are
 * sparse, and `isFixedIncome(null) === true` so unhydrated rows render
 * FI-shaped fields during the ref-data hydration window — matching the
 * defensive behavior the previous `isFixedIncomeAssetType` provided.
 */

// AssetClass

export const AssetClass = {
  Treasury: 'treasury',
  Corporate: 'corporate',
  Municipal: 'municipal',
  Agency: 'agency',
  CD: 'cd',
  Government: 'government',
  MoneyMarket: 'money_market',
  Securitized: 'securitized',
  Stock: 'stock',
  ETF: 'etf',
  ETC: 'etc',
  ADR: 'adr',
  PreferredShares: 'preferred_shares',
  Units: 'units',
  MutualFund: 'mutual_fund',
} as const

export type AssetClass = (typeof AssetClass)[keyof typeof AssetClass]

// Classification

type ClassificationInput = {
  assetType: AssetType | null
  fixedIncomeType: BondType | null
  instrumentClass: InstrumentClass | null
}

const BOND_TYPE_TO_ASSET_CLASS: Record<BondType, AssetClass> = {
  [BondType.Treasury]: AssetClass.Treasury,
  [BondType.Corporate]: AssetClass.Corporate,
  [BondType.Municipal]: AssetClass.Municipal,
  [BondType.Agency]: AssetClass.Agency,
  [BondType.CD]: AssetClass.CD,
  [BondType.Government]: AssetClass.Government,
  [BondType.MoneyMarket]: AssetClass.MoneyMarket,
  [BondType.Securitized]: AssetClass.Securitized,
}

const INSTRUMENT_CLASS_TO_ASSET_CLASS: Record<InstrumentClass, AssetClass> = {
  [InstrumentClass.Stock]: AssetClass.Stock,
  [InstrumentClass.DepositoryReceipt]: AssetClass.ADR,
  [InstrumentClass.PreferredShares]: AssetClass.PreferredShares,
  [InstrumentClass.Units]: AssetClass.Units,
  [InstrumentClass.Fund]: AssetClass.MutualFund,
  [InstrumentClass.ExchangeTradedFund]: AssetClass.ETF,
  [InstrumentClass.ExchangeTradedCommodity]: AssetClass.ETC,
}

export function getAssetClass(input: ClassificationInput): AssetClass | null {
  const { assetType, fixedIncomeType, instrumentClass } = input

  if (assetType === AssetType.FixedIncome) {
    return fixedIncomeType ? BOND_TYPE_TO_ASSET_CLASS[fixedIncomeType] : null
  }

  // Stock, DepositoryReceipt, PreferredShares and Units carry
  // assetType='equity'; Fund + ExchangeTradedFund carry assetType='fund'. Both
  // branches dispatch on instrumentClass to resolve to a granular AssetClass —
  // instrument_class is the only field that distinguishes ETF from Mutual Fund,
  // since both share assetType='fund'.
  if (assetType === AssetType.Equity || assetType === AssetType.Fund) {
    return instrumentClass
      ? INSTRUMENT_CLASS_TO_ASSET_CLASS[instrumentClass]
      : null
  }

  return null
}

/**
 * Convenience adapter for callers with a hydrated order in scope. Reads the
 * three reference-data fields from a partial order view and delegates to
 * `getAssetClass`. The `bondType` field on `DecoratedAggregatedOrderView`
 * carries the FI subtype; the BE-side payload calls it `type`, but the
 * hydration step (`use-hydrate-ref-data.ts`) maps it to `bondType` on the
 * decorated view.
 */
export function getAssetClassFromOrder(order: {
  assetType: AssetType | null
  bondType: BondType | null
  instrumentClass: InstrumentClass | null
}): AssetClass | null {
  return getAssetClass({
    assetType: order.assetType,
    fixedIncomeType: order.bondType,
    instrumentClass: order.instrumentClass,
  })
}

// AssetClass predicates

const EQUITY_ASSET_CLASSES = new Set<AssetClass>([
  AssetClass.Stock,
  AssetClass.ETF,
  AssetClass.ETC,
  AssetClass.ADR,
  AssetClass.PreferredShares,
  AssetClass.Units,
  AssetClass.MutualFund,
])

/**
 * True for explicitly-equity asset classes (Stock, ETF, ETC, ADR, Preferred
 * Shares, Units, MutualFund).
 * Returns `false` for `null` — callers fall back to FI/legacy rendering,
 * defending against pre-hydration regressions.
 */
export function isEquity(assetClass: AssetClass | null): boolean {
  if (!assetClass) return false
  return EQUITY_ASSET_CLASSES.has(assetClass)
}

const EXCHANGE_TRADED_EQUITY_CLASSES = new Set<AssetClass>([
  AssetClass.Stock,
  AssetClass.ETF,
  AssetClass.ETC,
  AssetClass.ADR,
  AssetClass.PreferredShares,
  AssetClass.Units,
])

/**
 * True for share-traded equity (stock / ETF / ETC / ADR / preferred / units) —
 * equity that quotes on a lit market with an NBBO, excluding mutual funds (which price at NAV).
 * Use to gate stock-style market-quote affordances. Returns `false` for `null`.
 */
export function isExchangeTradedEquity(assetClass: AssetClass | null): boolean {
  if (!assetClass) return false
  return EXCHANGE_TRADED_EQUITY_CLASSES.has(assetClass)
}

/**
 * True for fixed-income asset classes OR `null` (pre-hydration FI default).
 * Equivalent to `!isEquity(assetClass)`. Use for "should this FI-only
 * field render?" guards on the blotter and narrative surfaces.
 */
export function isFixedIncome(assetClass: AssetClass | null): boolean {
  return !isEquity(assetClass)
}

/**
 * True only for `AssetClass.MutualFund`. Use to gate fields or affordances
 * that specifically don't apply to MF orders, e.g. the editable allocation
 * form or the Timeline-tab MF submission surface. Distinct from
 * `isMutualFundAmount`, which inspects `amount.type` rather than the
 * instrument's semantic class.
 *
 * For "is this share-traded equity (stock / ETF / ETC / ADR)?", use
 * `isExchangeTradedEquity` rather than composing `isEquity(ac) && !isMutualFund(ac)`
 * ad hoc. Order tickets additionally classify via `classifyOrderAsset`
 * (`trading/order-entry`), the single place that partitions asset class into the
 * order discriminant.
 */
export function isMutualFund(assetClass: AssetClass | null): boolean {
  return assetClass === AssetClass.MutualFund
}

/**
 * True only for `AssetClass.MoneyMarket` (money market funds). MMFs resolve as
 * fixed income (`type === BondType.MoneyMarket`) but are their own order-entry
 * category — always Single. Identify them by asset class, never by
 * `instrumentClass`, which is null for MMFs.
 */
export function isMoneyMarketFund(assetClass: AssetClass | null): boolean {
  return assetClass === AssetClass.MoneyMarket
}

/**
 * Previous-day NAV for a mutual-fund order, from its ref-data decoration:
 * the close price, falling back to the evaluated price when the close is
 * missing or zero; `null` when neither is hydrated. Single source for every
 * surface that prices a fund (order-preview header, bulk execution), so the
 * fallback semantics can't drift between them.
 */
export function getMutualFundNav(order: {
  closePrice: number | null
  evalPrice: number | null
}): number | null {
  return order.closePrice || order.evalPrice || null
}

// Format-shape predicates

type AmountLike = {
  type?: string | null
  value?: number | null
} | null

const KNOWN_AMOUNT_TYPES = new Set<string>(Object.values(OrderSizeType))

function isOrderSizeType(value: string): value is OrderSizeType {
  return KNOWN_AMOUNT_TYPES.has(value)
}

/**
 * Normalize an `amount.type` string (which is wider than the enum because
 * the hand-written `Amount` interface uses `string`) into an `OrderSizeType`.
 * Unknown / nullish values default to `Par`, matching the legacy
 * "treat unknown as fixed income" convention used by price and fee helpers.
 *
 * Unknown non-nullish strings are warn-logged so a new BE-side amount type
 * shows up in observability before users notice mis-rendered cells.
 */
export function toOrderSizeType(
  input: string | null | undefined,
): OrderSizeType {
  if (input && isOrderSizeType(input)) {
    return input
  }
  if (input) {
    console.warn(
      '[instrument-classification] Unknown amount type, defaulting to Par',
      {
        amountType: input,
      },
    )
  }
  return OrderSizeType.Par
}

/**
 * True when the order's `amount.type` is `par` (or missing / unknown, which
 * is treated as legacy fixed income). Use this to gate fields that only
 * make sense for fixed-income orders — e.g. accrued interest, fees,
 * price-of-par decimals. Per FR-044, this is the *payload-shape* direction:
 * inspect `amount.type` directly. The *semantic-class* direction is
 * `isFixedIncome(getAssetClassFromOrder(order))`.
 */
export function isFixedIncomeAmount(amount: AmountLike | undefined): boolean {
  const type = amount?.type
  if (type === null || type === undefined) return true
  if (type === OrderSizeType.Par) return true
  return !isOrderSizeType(type)
}

/**
 * True when the order's `amount.type` is `notional`, the mutual-fund shape.
 * Use this to gate fields whose value is degenerate or misleading for
 * mutual fund orders — e.g. net money (which equals notional for MF, so
 * displaying both would just duplicate the same number). See
 * `isMutualFund` for the semantic-class direction.
 */
export function isMutualFundAmount(amount: AmountLike | undefined): boolean {
  return amount?.type === OrderSizeType.Notional
}

// Display

/**
 * Binary asset-type bucket: `'fixed_income' | 'equity'`. Use this for
 * surfaces that need a coarser FI-vs-Equity distinction than the granular
 * `AssetClass`.
 *
 * The `'equity'` value names the semantic equity bucket (Stock / ETF / ETC /
 * ADR / MutualFund per `isEquity`), which is broader than the backend's
 * `AssetType.Equity` (Stock + ADR only — ETF, ETC, and MutualFund all carry
 * `assetType === 'fund'`). To classify an order against this type, use
 * `isEquity(getAssetClassFromOrder(o))`, not
 * `order.assetType === AssetType.Equity`, which would miss ETFs, ETCs, and MFs.
 */
export type AssetTypeBinary = Exclude<AssetType, 'fund'>

const ASSET_TYPE_BINARY_VALUES = new Set<string>([
  AssetType.FixedIncome,
  AssetType.Equity,
])

/**
 * Type guard for narrowing an arbitrary string (e.g. localStorage payload,
 * design-system control value) to `AssetTypeBinary`. Use at boundaries where
 * the input is `string` rather than already typed.
 */
export function isAssetTypeBinary(value: string): value is AssetTypeBinary {
  return ASSET_TYPE_BINARY_VALUES.has(value)
}

const ASSET_TYPE_LABELS: Record<AssetClass, 'Fixed Income' | 'Equity'> = {
  [AssetClass.Treasury]: 'Fixed Income',
  [AssetClass.Corporate]: 'Fixed Income',
  [AssetClass.Municipal]: 'Fixed Income',
  [AssetClass.Agency]: 'Fixed Income',
  [AssetClass.CD]: 'Fixed Income',
  [AssetClass.Government]: 'Fixed Income',
  [AssetClass.MoneyMarket]: 'Fixed Income',
  [AssetClass.Securitized]: 'Fixed Income',
  [AssetClass.Stock]: 'Equity',
  [AssetClass.ETF]: 'Equity',
  [AssetClass.ETC]: 'Equity',
  [AssetClass.ADR]: 'Equity',
  [AssetClass.PreferredShares]: 'Equity',
  [AssetClass.Units]: 'Equity',
  [AssetClass.MutualFund]: 'Equity',
}

export function getAssetTypeLabel(
  assetClass: AssetClass | null,
): 'Fixed Income' | 'Equity' | null {
  if (!assetClass) return null
  return ASSET_TYPE_LABELS[assetClass]
}

const ASSET_CLASS_LABELS: Record<AssetClass, string> = {
  [AssetClass.Treasury]: 'Treasury',
  [AssetClass.Corporate]: 'Corporate',
  [AssetClass.Municipal]: 'Municipal',
  [AssetClass.Agency]: 'Agency',
  [AssetClass.CD]: 'CD',
  // Government displays as "Sovereign" per QT-1084
  [AssetClass.Government]: 'Sovereign',
  // MoneyMarket displays as "MMF"
  [AssetClass.MoneyMarket]: 'MMF',
  [AssetClass.Securitized]: 'Securitized',
  [AssetClass.Stock]: 'Stock',
  [AssetClass.ETF]: 'ETF',
  [AssetClass.ETC]: 'ETC',
  [AssetClass.ADR]: 'ADR',
  [AssetClass.PreferredShares]: 'Preferred Shares',
  [AssetClass.Units]: 'Units',
  [AssetClass.MutualFund]: 'MF',
}

export function getAssetClassLabel(
  assetClass: AssetClass | null,
): string | null {
  if (!assetClass) return null
  return ASSET_CLASS_LABELS[assetClass]
}
